import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/** Doc §4 On/Off — options from global.app_lookup FEATURE_FLAG. */
const TriSelect = ({
  label,
  value,
  onChange,
  allowBlank = true,
  disabled = false,
  options = [],
}) => {
  const items =
    options.length > 0
      ? options.filter((o) => allowBlank || o.value !== '')
      : [];
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {items.map((o) => (
          <MenuItem key={o.value === '' ? 'PLAN_DEFAULT' : o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const Field = ({ children }) => (
  <Grid item xs={6} sm={4} md={3}>
    {children}
  </Grid>
);

const Section = ({ title }) => (
  <Grid item xs={12}>
    <Divider sx={{ my: 1 }} />
    <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.4}>
      {title.toUpperCase()}
    </Typography>
  </Grid>
);

/**
 * Compact license capacity/feature form (doc §4 labels).
 * @param {'register'|'license'} mode
 * @param {{ HUB?: boolean, SYNC_FILE?: boolean, AGENTIC_AI?: boolean }} enabledProducts
 */
const OptionalExtrasFields = ({
  extras,
  setExtra,
  defaultExpanded = false,
  mode = 'register',
  enabledProducts = null,
  featureFlagOptions = [],
  integrationAssets = null,
  disabled = false,
}) => {
  const set = (key, value) => {
    if (disabled) return;
    setExtra(key, value);
  };
  const allowBlank = mode === 'register';
  const showDates = mode === 'register';
  const hubOn = !disabled && (enabledProducts == null || !!enabledProducts.HUB);
  const syncOn = !disabled && (enabledProducts == null || !!enabledProducts.SYNC_FILE);
  const aiOn = !disabled && (enabledProducts == null || !!enabledProducts.AGENTIC_AI);
  const flagOpts = featureFlagOptions;

  const form = (
    <Box
      component="fieldset"
      disabled={disabled}
      sx={{
        border: 0,
        m: 0,
        p: 0,
        minWidth: 0,
        opacity: disabled ? 0.65 : 1,
      }}
    >
    <Grid container spacing={1.5}>
      {showDates && (
        <>
          <Section title="Subscription dates" />
          <Field>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Grace period (days)"
              value={extras.gracePeriodDays ?? ''}
              onChange={(e) => set('gracePeriodDays', e.target.value)}
            />
          </Field>
          <Field>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start date"
              InputLabelProps={{ shrink: true }}
              value={extras.licenseStartDate || ''}
              onChange={(e) => set('licenseStartDate', e.target.value)}
            />
          </Field>
          <Field>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End / renewal"
              InputLabelProps={{ shrink: true }}
              value={extras.licenseEndDate || ''}
              onChange={(e) => set('licenseEndDate', e.target.value)}
            />
          </Field>
        </>
      )}

      <Section title="Users" />
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Licensed users"
          disabled={disabled}
          value={extras.maxUsers ?? ''}
          onChange={(e) => set('maxUsers', e.target.value)}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Tenant admins"
          disabled={disabled}
          value={extras.maxTenantAdmins ?? ''}
          onChange={(e) => set('maxTenantAdmins', e.target.value)}
        />
      </Field>

      <Section title="Agentic AI" />
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="AI agents"
          disabled={!aiOn}
          value={extras.maxAiAgents ?? ''}
          onChange={(e) => set('maxAiAgents', e.target.value)}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Agent teams"
          disabled={!aiOn}
          value={extras.maxAgentTeams ?? ''}
          onChange={(e) => set('maxAgentTeams', e.target.value)}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="AI tokens / month"
          disabled={!aiOn}
          value={extras.maxAiTokensMonth ?? ''}
          onChange={(e) => set('maxAiTokensMonth', e.target.value)}
        />
      </Field>
      <Field>
                <TriSelect
          label="Knowledge base"
          value={extras.knowledgeBaseEnabled}
          onChange={(v) => set('knowledgeBaseEnabled', v)}
          allowBlank={allowBlank}
          disabled={!aiOn}
          options={flagOpts}
        />
      </Field>

      <Section title="Integration" />
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Connectors"
          disabled={!hubOn}
          value={extras.maxConnectors ?? ''}
          onChange={(e) => set('maxConnectors', e.target.value)}
          helperText="Max connector instances"
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Flows"
          disabled={!hubOn}
          value={extras.maxFlows ?? ''}
          onChange={(e) => set('maxFlows', e.target.value)}
          helperText="Max flow instances"
        />
      </Field>
      <Field>
                <TriSelect
          label="API access"
          value={extras.apiAccessEnabled}
          onChange={(v) => set('apiAccessEnabled', v)}
          allowBlank={allowBlank}
          disabled={!hubOn}
          options={flagOpts}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="API calls / month"
          disabled={!hubOn}
          value={extras.maxApiCallsMonth ?? ''}
          onChange={(e) => set('maxApiCallsMonth', e.target.value)}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="MCP servers"
          disabled={!aiOn}
          value={extras.maxMcpServers ?? ''}
          onChange={(e) => set('maxMcpServers', e.target.value)}
        />
      </Field>

      {integrationAssets ? (
        <Grid item xs={12}>
          {integrationAssets}
        </Grid>
      ) : null}

      <Section title="Sync File" />
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Storage (GB)"
          disabled={!syncOn}
          value={extras.storageGb ?? ''}
          onChange={(e) => set('storageGb', e.target.value)}
        />
      </Field>
      <Field>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="RAG storage (GB)"
          disabled={!syncOn}
          value={extras.ragStorageGb ?? ''}
          onChange={(e) => set('ragStorageGb', e.target.value)}
        />
      </Field>
      <Field>
                <TriSelect
          label="File share"
          value={extras.fileShareEnabled}
          onChange={(v) => set('fileShareEnabled', v)}
          allowBlank={allowBlank}
          disabled={!syncOn}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="File upload"
          value={extras.fileUploadEnabled}
          onChange={(v) => set('fileUploadEnabled', v)}
          allowBlank={allowBlank}
          disabled={!syncOn}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="Benchmark"
          value={extras.benchmarkEnabled}
          onChange={(v) => set('benchmarkEnabled', v)}
          allowBlank={allowBlank}
          disabled={!syncOn}
          options={flagOpts}
        />
      </Field>

      <Section title="Environment & security" />
      <Field>
                <TriSelect
          label="Production"
          value={extras.productionEnabled}
          onChange={(v) => set('productionEnabled', v)}
          allowBlank={allowBlank}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="Sandbox"
          value={extras.sandboxEnabled}
          onChange={(v) => set('sandboxEnabled', v)}
          allowBlank={allowBlank}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="SSO"
          value={extras.ssoEnabled}
          onChange={(v) => set('ssoEnabled', v)}
          allowBlank={allowBlank}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="MFA"
          value={extras.mfaEnabled}
          onChange={(v) => set('mfaEnabled', v)}
          allowBlank={allowBlank}
          options={flagOpts}
        />
      </Field>
      <Field>
                <TriSelect
          label="Audit logs"
          value={extras.auditLogsEnabled}
          onChange={(v) => set('auditLogsEnabled', v)}
          allowBlank={allowBlank}
          options={flagOpts}
        />
      </Field>
    </Grid>
    </Box>
  );

  if (mode === 'license') {
    return <Box>{form}</Box>;
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">License configuration (optional)</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Leave blank (or Plan default) to keep plan values.
        </Typography>
        {form}
      </AccordionDetails>
    </Accordion>
  );
};

export default OptionalExtrasFields;
